import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { BookMarked, Loader2, Info } from 'lucide-react';
import { cardDialectApi } from '../../services/iso8583Api';
import { PageHeader, Card, Table, originalElement, originalHow } from './common';

/**
 * The dialect this switch speaks, stated by the switch — and the others it can
 * describe, in tabs beside it.
 *
 * Certifying "ISO 8583" in the abstract means little: what matters is the
 * dialect of the switch a participant is joining. The engine reads one profile
 * — codec, generator, explainer and certification all — so this page is what it
 * really does rather than a description of it.
 *
 * The tabs are here because a participant's question is usually comparative:
 * not "what does this switch expect" but "how does that differ from the switch
 * I already run". Only one profile is ever in force, and the tabs say which.
 */
export default function CardDialect() {
  const [available, setAvailable] = useState([]);
  const [byId, setById] = useState({});
  const [selected, setSelected] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    cardDialectApi.get()
      .then((d) => {
        setAvailable(d.available?.length ? d.available : [{ id: d.id, inForce: true }]);
        setById({ [d.id]: d });
        setSelected(d.id);
      })
      .catch((e) => setError(e.response?.data?.error || e.message));
  }, []);

  // Profiles are immutable, so one fetch per tab is enough for the visit.
  const show = (id) => {
    setSelected(id);
    if (byId[id]) return;
    cardDialectApi.getOne(id)
      .then((d) => setById((m) => ({ ...m, [id]: d })))
      .catch((e) => setError(e.response?.data?.error || e.message));
  };

  if (error) return <p className="text-sm text-red-700">{error}</p>;
  if (!selected) return <Loader2 className="w-5 h-5 animate-spin text-gray-400" />;

  const dialect = byId[selected];

  return (
    <div className="space-y-6 max-w-5xl">
      <PageHeader icon={BookMarked} title="Dialect">
        What this switch expects on the wire, from the profile it actually reads. Every page on this corridor —
        the generator's templates, the field catalogue, the frame explainer — follows the profile in force.
      </PageHeader>

      <div className="border-b border-gray-200">
        <nav className="-mb-px flex flex-wrap gap-x-8" role="tablist" aria-label="Profiles on this deployment">
          {available.map((d) => {
            const active = d.id === selected;
            return (
              <button key={d.id} type="button" role="tab" aria-selected={active} onClick={() => show(d.id)}
                className={`group flex flex-col items-start pt-1 pb-3 border-b-2 cursor-pointer transition-colors
                  focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 rounded-t ${
                  active ? 'border-primary-600' : 'border-transparent hover:border-gray-300'}`}>
                <span className={`flex items-center gap-2 text-sm font-semibold ${
                  active ? 'text-primary-700' : 'text-gray-500 group-hover:text-gray-800'}`}>
                  {profileName(d.id)}
                  {d.inForce && (
                    <span className="px-1.5 py-0.5 rounded-full bg-green-100 text-green-800 text-[10px] font-medium">
                      in force
                    </span>
                  )}
                </span>
                <span className={`font-mono text-[11px] ${active ? 'text-gray-500' : 'text-gray-400'}`}>{d.id}</span>
              </button>
            );
          })}
        </nav>
      </div>

      {dialect?.switchedAt && dialect.defaultId && (
        <p className="text-xs text-gray-500 -mt-3">
          The link is speaking <span className="font-mono">{dialect.id}</span> for now; the deployment's default
          is <span className="font-mono">{dialect.defaultId}</span>, which a restart of the engine returns to.
        </p>
      )}

      {!dialect ? <Loader2 className="w-5 h-5 animate-spin text-gray-400" /> : (
        <>
          <Card title={dialect.inForce ? 'The profile in force' : 'This profile, for comparison'}>
            {dialect.description && <p className="text-sm text-gray-600 mb-4">{dialect.description}</p>}
            <Table head={['', '']} mono={[]} rows={[
              ['Id', <span className="font-mono">{dialect.id}</span>],
              ['Supplied by', dialect.source === 'operator-supplied'
                ? 'AfricaNenda, as a profile file on the deployment'
                : 'built into the engine'],
              ['In force here', dialect.inForce
                ? 'Yes — this is the dialect the card link speaks'
                : 'No. Shown for comparison; the link speaks whichever profile is marked in force above.'],
              ['Framing', <span className="font-mono">{dialect.framing}</span>],
              ['MTI', <>{dialect.mtiEncoding} · {dialect.headerBytes > 0
                ? `${dialect.headerBytes}-byte header before it` : 'no header before it'}</>],
              ['Bitmaps', <>{dialect.bitmapEncoding}, {dialect.bitmapEncoding === 'ASCII_HEX'
                ? '16 characters' : '8 bytes'} each; the secondary present when bit 1 is set</>],
              ['Data elements defined', dialect.fieldCount],
              ...(dialect.networkManagement ? [
                ['Network management', <>{dialect.networkManagement.requestMti} / {dialect.networkManagement.responseMti},
                  the function in DE <span className="font-mono">{dialect.networkManagement.field}</span>:{' '}
                  {Object.entries(dialect.networkManagement.codes || {}).map(([k, v]) => `${k} ${v}`).join(', ')}</>],
              ] : []),
              ...(dialect.responseCodes ? [
                ['Response codes', <>DE <span className="font-mono">{dialect.responseCodes.field}</span>:{' '}
                  {Object.entries(dialect.responseCodes.codes || {}).map(([k, v]) => `${k} ${v}`).join(', ')}</>],
              ] : []),
              ...(dialect.transactionKey ? [
                ['A transaction is identified by', <>DE {dialect.transactionKey.fields.join(', DE ')}
                  {dialect.transactionKey.optional?.length > 0 && <> (with DE {dialect.transactionKey.optional.join(', DE ')} when present)</>}</>],
              ] : []),
              ...(dialect.originalData ? [
                ['A reversal names its original', dialect.originalData.layout === 'fields'
                  ? <>in {originalHow(dialect.originalData)}</>
                  : <>in {originalElement(dialect.originalData)} — {originalHow(dialect.originalData)}</>],
              ] : []),
              ...(dialect.reversal
                ? [['Reversal says why in',
                  <>DE <span className="font-mono">{dialect.reversal.reasonField}</span>
                    {dialect.reversal.timeoutReason && <>; a time-out reads{' '}
                      <span className="font-mono">{dialect.reversal.timeoutReason}</span></>}</>]]
                : [['Reversal', 'This profile states no reversal convention, so the switch cannot originate '
                    + 'reversals on it.']]),
            ]} />
          </Card>

          {!dialect.published && (
            <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 flex items-start gap-2">
              <Info className="w-5 h-5 text-blue-600 shrink-0 mt-0.5" />
              <p className="text-sm text-blue-900">{dialect.note}</p>
            </div>
          )}

          {dialect.published && (
            <Card title={`Data elements (${dialect.fields.length})`}>
              <Table head={['DE', 'Name', 'Format', 'Length', 'Content', 'Encoding']} mono={[0, 2, 3, 5]}
                rows={dialect.fields.map((f) => [
                  f.de,
                  <>
                    {f.name}
                    {f.subFields?.length > 0 && (
                      <span className="text-gray-500"> · {f.subFields.length} sub-field
                        {f.subFields.length === 1 ? '' : 's'}</span>
                    )}
                  </>,
                  f.format,
                  f.type === 'FIXED' ? `${f.length}` : `up to ${f.length}`,
                  f.content,
                  f.encoding,
                ])} />
              <p className="text-xs text-gray-500 mt-3">
                To see these applied to real bytes, paste a frame into{' '}
                <Link to="/iso8583/explain" className="text-primary-600 hover:underline">Explain a Frame</Link>,
                or build one in the{' '}
                <Link to="/iso8583/message-generator" className="text-primary-600 hover:underline">Message
                Generator</Link>. Both follow the profile in force, not whichever tab you are reading.
              </p>
            </Card>
          )}
        </>
      )}

      <Card title="Need a different switch?">
        <p className="text-sm text-gray-600">
          Each tab is a profile this switch can speak; the badge marks the one the card link speaks now. If the
          switch you are integrating with is not among them, ask AfricaNenda.
        </p>
      </Card>
    </div>
  );
}

/** A name a participant recognises, from the profile id; the id itself stays on the tab beneath it. */
function profileName(id) {
  if (id === 'generic-ascii-1987') return 'ISO 8583:1987 baseline';
  if (id.startsWith('postilion')) return 'Postilion';
  if (id.startsWith('smartvista')) return 'SmartVista';
  if (id.startsWith('base24')) return 'BASE24-eps';
  if (id.startsWith('openway')) return 'OpenWay (binary 1987)';
  return id;
}
